"use client";

import { clsx } from "clsx";
import { motion } from "motion/react";

import { toStatusKey, type StatusKey } from "./status";

// Traffic-light palette, matching the platform's score chips.
const DOT_CLASS: Record<StatusKey, string> = {
  operational: "bg-green-500",
  degraded: "bg-amber-500",
  down: "bg-red-500",
  unknown: "bg-muted-foreground",
};

/**
 * `pulse` shows a repeating ping ring while `hovered`; motion eases it back to the resting dot on
 * un-hover instead of snapping.
 */
export function StatusDot({
  state,
  pulse = false,
  hovered = false,
}: {
  state: string | undefined;
  pulse?: boolean;
  hovered?: boolean;
}) {
  const color = DOT_CLASS[toStatusKey(state)];
  return (
    <span className="relative flex size-2 shrink-0" aria-hidden>
      {pulse && (
        <motion.span
          className={clsx("absolute inset-0 rounded-full", color)}
          initial={false}
          // opacity starts and ends at 0 so the loop seam is invisible (no flicker).
          animate={
            hovered
              ? { scale: [1, 2.4], opacity: [0, 0.6, 0] }
              : { scale: 1, opacity: 0 }
          }
          transition={
            hovered
              ? { duration: 1.6, ease: "easeOut", repeat: Infinity }
              : { duration: 0.4, ease: "easeOut" }
          }
        />
      )}
      <span
        className={clsx("relative inline-flex size-2 rounded-full", color)}
      />
    </span>
  );
}
